import {inject, Injectable} from './di';
import {Logger} from './logger';
import {AppInitializeService} from './app-initialize-service';

/**
 * Service for tracking global unhandled exceptions and rejections
 */
@Injectable()
export class GlobalErrorHandlerService implements AppInitializeService {
  private readonly logger = inject(Logger);

  private originalOnErrorHandler: undefined | typeof window.onerror;

  public async init() {
    try {
      this.logger.debug('[GlobalErrorHandlerService] GlobalErrorHandlerService initialization started');

      // NOTE: For unknown reasons, some old browsers (for example, Firefox 5) do not contain information about the
      // error itself in the event parameter when trying to catch certain types of errors using
      // addEventListener('error'). But the old `window.onerror` callback does contain this information. The only
      // problem with such callbacks is that there can only be one. Therefore, you can accidentally erase someone
      // else's callback (inserted by a third-party framework, for example).
      this.originalOnErrorHandler = window.onerror;
      window.onerror = this.onRootError.bind(this);

      // TODO: replace `onerror` with `addEventListener` if we drop support of browsers below stable ES6
      // Catch all unhandled errors
      // window.addEventListener(
      //   'error',
      //   (event: ErrorEvent | Event) => {
      //     // Note: Event type comes in case of `<link>`, `<script>`, <video> tags source loading error
      //     const {message, lineno, colno, error, target, filename} = event as ErrorEvent;
      //     if (message || error) {
      //       this.logger.error('[root] Unhandled runtime error', {message, filename, lineno, colno, error});
      //     } else if (target instanceof HTMLLinkElement) {
      //       this.logger.error('[root] Unable to load style', target.href);
      //     } else if (target instanceof HTMLScriptElement) {
      //       this.logger.error('[root] Unable to load script file', target.src);
      //     } else if (target instanceof HTMLVideoElement) {
      //       this.logger.warn('[root] Unable to load video file', target.src);
      //     } else if (target instanceof HTMLImageElement) {
      //       this.logger.warn('[root] Unable to load image', target.src, target.srcset);
      //     } else {
      //       this.logger.error('[root] Unhandled unknown runtime error', event);
      //     }
      //   },
      //   true,
      // );

      // Catch all unhandled promises
      window.addEventListener('unhandledrejection', this.onUnhandledRejection.bind(this));

      this.logger.debug('[GlobalErrorHandlerService] GlobalErrorHandlerService initialization completed');
    } catch (err) {
      this.logger.critical('[GlobalErrorHandlerService] GlobalErrorHandlerService initialization failed', err);
      return Promise.resolve(false);
    }

    return Promise.resolve(true);
  }

  /**
   * Handle root unhandled exceptions
   */
  private onRootError(
    message: string | Event,
    filename: string | undefined,
    lineno: number | undefined,
    colno: number | undefined,
    error: Error | undefined,
  ) {
    // handle other event handlers
    if (this.originalOnErrorHandler && typeof this.originalOnErrorHandler === 'function') {
      try {
        this.originalOnErrorHandler(message, filename, lineno, colno, error);
      } catch (e) {
        this.logger.error('[root] Another `window.onerror` handler error', e);
      }
    }

    const errorEvent = message as ErrorEvent | undefined;
    filename = errorEvent?.filename || filename;
    lineno = errorEvent?.lineno || lineno;
    colno = errorEvent?.colno || colno;
    error = (errorEvent?.error || error) as Error | undefined;

    const target = errorEvent?.target;

    const msg =
      this.ensureString(errorEvent?.message) ||
      this.ensureString(message) ||
      this.ensureString(error?.message) ||
      this.ensureString(error?.name) ||
      '';
    const stack = this.ensureString((error as Error)?.stack);

    if (
      window.HTMLLinkElement &&
      typeof window.HTMLLinkElement === 'function' &&
      target instanceof window.HTMLLinkElement
    ) {
      this.logger.error(`[root] Unable to load style: "${target.href}"`, msg);
    } else if (
      window.HTMLScriptElement &&
      typeof window.HTMLScriptElement === 'function' &&
      target instanceof window.HTMLScriptElement
    ) {
      this.logger.error(`[root] Unable to load script file: "${target.src}"`, msg);
    } else if (
      window.HTMLVideoElement &&
      typeof window.HTMLVideoElement === 'function' &&
      target instanceof window.HTMLVideoElement
    ) {
      this.logger.error(`[root] Unable to load video file: "${target.src || target.currentSrc}"`, msg);
    } else if (
      window.HTMLImageElement &&
      typeof window.HTMLImageElement === 'function' &&
      target instanceof window.HTMLImageElement
    ) {
      this.logger.error(`[root] Unable to load image: "${target.src || target.srcset}"`, msg);
    } else {
      this.logger.error('[root] Unhandled unknown runtime error!', {stack, message, filename, lineno, colno, error});
    }
  }

  private ensureString(s: any) {
    return typeof s === 'string' ? s : undefined;
  }

  private onUnhandledRejection(event: PromiseRejectionEvent) {
    this.logger.error('[root] Unhandled Promise rejection', event);
  }
}
